import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Search, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Convert a slug like "jaipur-malviya-nagar" to a display label "Malviya Nagar".
// The first token is the city (jaipur/darbhanga/...) and the rest is the area.
const labelFromSlug = (slug = '', cityKey = '') => {
  const cleaned = cityKey ? slug.replace(new RegExp(`^${cityKey}-?`), '') : slug;
  return cleaned
    .split('-')
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const ServiceAreas = ({ variant = 'home' }) => {
  const [pages, setPages] = useState([]);
  const [activeCity, setActiveCity] = useState('jaipur');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/city-pages`)
      .then(r => (r.ok ? r.json() : []))
      .then(data => {
        setPages(Array.isArray(data) ? data.filter(p => p.is_published !== false) : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // Group pages by their city prefix (first slug token). Treat master "jaipur" and
  // "darbhanga" slugs as the city hubs themselves.
  const cities = useMemo(() => {
    const groups = {};
    pages.forEach(p => {
      const key = (p.slug || '').split('-')[0];
      if (!key) return;
      if (!groups[key]) groups[key] = { key, label: key.charAt(0).toUpperCase() + key.slice(1), localities: [], hub: null };
      // Master city page vs locality page
      if (p.slug === key) {
        groups[key].hub = p;
      } else {
        groups[key].localities.push(p);
      }
    });
    Object.values(groups).forEach(g => g.localities.sort((a, b) => (a.city_name || a.slug).localeCompare(b.city_name || b.slug)));
    return groups;
  }, [pages]);

  const cityKeys = Object.keys(cities).sort((a, b) => {
    // Force Jaipur first, Darbhanga second, then alphabetical
    const order = { jaipur: 0, darbhanga: 1 };
    const oa = order[a] ?? 99;
    const ob = order[b] ?? 99;
    if (oa !== ob) return oa - ob;
    return a.localeCompare(b);
  });
  const activeGroup = cities[activeCity] || cities[cityKeys[0]];

  const filtered = useMemo(() => {
    if (!activeGroup) return [];
    const q = query.trim().toLowerCase();
    if (!q) return activeGroup.localities;
    return activeGroup.localities.filter(p =>
      (p.city_name || '').toLowerCase().includes(q) ||
      (p.slug || '').toLowerCase().includes(q)
    );
  }, [activeGroup, query]);

  if (loading) return null;
  if (cityKeys.length === 0) return null;

  const isHome = variant === 'home';

  return (
    <section
      id="service-areas"
      className={isHome ? 'py-16 bg-gradient-to-b from-blue-50 to-white' : 'py-12'}
      data-testid="service-areas-section"
    >
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
            <MapPin size={16} /> Service Areas
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-testid="service-areas-title">
            Find RO Service in <span className="text-blue-700">Your Area</span>
          </h2>
          <p className="text-gray-600 text-base md:text-lg">
            We serve <strong>{pages.length}+ neighbourhoods</strong>. Tap your locality to see local pricing,
            technicians, and same-day booking.
          </p>
        </div>

        {/* City tabs */}
        {cityKeys.length > 1 && (
          <div className="flex flex-wrap justify-center gap-2 mb-6" data-testid="service-areas-tabs">
            {cityKeys.map(k => {
              const g = cities[k];
              const isActive = k === (activeGroup?.key);
              return (
                <button
                  key={k}
                  onClick={() => { setActiveCity(k); setQuery(''); }}
                  data-testid={`city-tab-${k}`}
                  className={`px-5 py-2 rounded-full font-semibold text-sm transition-all ${
                    isActive
                      ? 'bg-blue-700 text-white shadow-md'
                      : 'bg-white text-gray-700 hover:bg-blue-50 border border-gray-200'
                  }`}
                >
                  {g.label} <span className="opacity-70 ml-1">({g.localities.length})</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Search filter */}
        <div className="max-w-md mx-auto mb-8 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder={`Search area in ${activeGroup?.label || ''}...`}
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 focus:border-blue-500 rounded-full text-sm outline-none transition-colors bg-white"
            data-testid="service-area-search"
          />
        </div>

        {/* Master city CTA */}
        {activeGroup?.hub && (
          <div className="max-w-3xl mx-auto mb-6">
            <Link
              to={`/city/${activeGroup.hub.slug}`}
              className="flex items-center justify-between gap-4 bg-gradient-to-r from-blue-700 to-blue-600 hover:from-blue-800 hover:to-blue-700 text-white px-6 py-4 rounded-xl shadow-md hover:shadow-lg transition-all"
              data-testid={`master-city-link-${activeGroup.key}`}
            >
              <div>
                <div className="text-xs uppercase tracking-wider opacity-80">Main City Hub</div>
                <div className="font-bold text-lg">{activeGroup.hub.hero_title_highlight || activeGroup.label} — All RO Services</div>
              </div>
              <ArrowRight size={22} />
            </Link>
          </div>
        )}

        {/* Locality grid */}
        {filtered.length === 0 ? (
          <p className="text-center text-gray-500 py-10" data-testid="service-area-empty">
            No localities match &quot;{query}&quot;.
          </p>
        ) : (
          <div
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 max-w-6xl mx-auto"
            data-testid="service-areas-grid"
          >
            {filtered.map(p => (
              <Link
                key={p.slug}
                to={`/city/${p.slug}`}
                data-testid={`area-link-${p.slug}`}
                className="group bg-white hover:bg-blue-700 border border-gray-200 hover:border-blue-700 rounded-lg px-3 py-3 text-center transition-all hover:shadow-md hover:-translate-y-0.5"
              >
                <div className="flex items-center justify-center gap-1.5">
                  <MapPin size={14} className="text-blue-600 group-hover:text-white shrink-0" />
                  <span className="text-sm font-semibold text-gray-800 group-hover:text-white truncate">
                    {p.city_name || labelFromSlug(p.slug, activeGroup?.key)}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}

        {isHome && (
          <div className="text-center mt-10">
            <Link
              to="/service-areas"
              className="inline-flex items-center gap-2 text-blue-700 hover:text-blue-800 font-semibold"
              data-testid="view-all-areas-link"
            >
              View all service areas <ArrowRight size={18} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default ServiceAreas;
